/**
 * `onboarding-state` — discriminated-union state + event types +
 * `applyEvent` reducer for the SHA v0 patient onboarding flow per
 * [`sha_v0_patient_onboarding_flow_design.md`](../../../../sha_v0_patient_onboarding_flow_design.md)
 * §2 state machine.
 *
 * Shape parallel to `auth/auth-flow.ts` + `recovery/state.ts` + `activation/activation-state.ts`
 * (existing flow scaffold pattern). Discriminated-union state +
 * event; `applyEvent` is pure-function reducer; `<OnboardingFlowView />`
 * subscribes to flow state changes per existing observer pattern.
 *
 * **Recovery setup election tier** — v1 default `low-risk` (3 layers:
 * knowledge + possession; medium-risk + high-risk in v1.5+) per memo
 * §3.2 + Step 3b §6.1 4-state recovery-setup-status taxonomy.
 *
 * **Out of scope (Sprint 8+):** recovery-setup ceremony substantive
 * crypto (composes with `IProjectionServiceClient.initiateRecoverySetup`
 * etc. wire-contract DEV3 surface); identity rotation onboarding
 * surfaces (v1.5+).
 */
export const ONBOARDING_TERMINAL_STATES = new Set([
    'account-home-landing',
    'cancelled',
    'error',
]);
export function isTerminal(state) {
    return ONBOARDING_TERMINAL_STATES.has(state.kind);
}
export class OnboardingTransitionError extends Error {
    constructor(currentKind, eventKind) {
        super(`invalid onboarding transition: cannot apply event "${eventKind}" in state "${currentKind}"`);
        this.name = 'OnboardingTransitionError';
    }
}
export function applyEvent(state, event) {
    // Universal cancel/error reachable from any non-terminal state
    if (!isTerminal(state)) {
        if (event.kind === 'cancel') {
            return { kind: 'cancelled', reason: event.reason };
        }
        if (event.kind === 'error') {
            return { kind: 'error', code: event.code, message: event.message };
        }
    }
    switch (state.kind) {
        case 'activation-pending':
            if (event.kind === 'activation-completed') {
                return { kind: 'recovery-setup-election' };
            }
            break;
        case 'recovery-setup-election':
            if (event.kind === 'submit-recovery-tier') {
                return { kind: 'recovery-setup-ceremony', tier: event.tier };
            }
            break;
        case 'recovery-setup-ceremony':
            if (event.kind === 'recovery-setup-completed') {
                return { kind: 'first-session-bootstrap' };
            }
            break;
        case 'first-session-bootstrap':
            if (event.kind === 'bootstrap-completed') {
                return { kind: 'initial-sign-in' };
            }
            break;
        case 'initial-sign-in':
            if (event.kind === 'sign-in-session-active') {
                return { kind: 'account-home-landing' };
            }
            break;
        default:
            // terminal states; no further transitions
            break;
    }
    throw new OnboardingTransitionError(state.kind, event.kind);
}
//# sourceMappingURL=onboarding-state.js.map